import type { ReactiveController, ReactiveControllerHost } from 'lit'

type AriaDescriptionElement = HTMLElement & {
  ariaDescribedByElements: Element[]
}

/**
 * Reflects light-DOM helper elements onto a native Shadow-DOM focusable.
 * The control also renders an `aria-describedby` IDREF and receives an
 * `aria-description` text fallback; browsers with cross-root ARIA element
 * reflection receive the real elements.
 */
export class AriaDescriptionController implements ReactiveController {
  private managedControl?: HTMLElement

  constructor(
    private host: ReactiveControllerHost,
    private getControl: () => HTMLElement | undefined,
    private getElements: () => readonly HTMLElement[] | undefined,
  ) {
    host.addController(this)
  }

  hostUpdated() {
    const control = this.getControl()
    const elements = this.getElements()
    if (!control || elements === undefined) {
      this.managedControl?.removeAttribute('aria-description')
      this.managedControl = undefined
      return
    }

    if (this.managedControl && this.managedControl !== control) {
      this.managedControl.removeAttribute('aria-description')
    }
    this.managedControl = control

    const description = elements.map(element => element.textContent?.trim()).filter(Boolean).join(' ')
    if (description) {
      control.setAttribute('aria-description', description)
    }
    else {
      control.removeAttribute('aria-description')
    }

    if ('ariaDescribedByElements' in control) {
      ;(control as AriaDescriptionElement).ariaDescribedByElements = [...elements]
    }
  }
}
